import { SearchSuggestionsList } from '@iheartjane/menu';
import {
  Box,
  Button,
  DismissIcon,
  Flex,
  SearchField,
  Typography,
} from '@iheartjane/reefer';
import { clsx } from 'clsx';
import { Form } from 'react-router';

import { SearchManager } from '@jane/bloom/components/searchManager';
import { useFullDestination } from '@jane/bloom/hooks/navigation/useFullDestination';
import { useStoreSwitcher } from '@jane/bloom/hooks/storeSwitcher/useStoreSwitcher';
import { useCustomLanguage } from '@jane/bloom/hooks/useCustomLanguage';
import { useDebounce } from '@jane/bloom/hooks/useDebounce';
import { useRootLoaderData } from '@jane/bloom/hooks/useRootLoaderData';
import { useUserSearchQuery } from '@jane/bloom/hooks/useUserSearchQuery';
import { SEARCH_TERM_QUERY_KEY } from '@jane/bloom/utils/queryParams/queryParams';
import { sleep } from '@jane/bloom/utils/sleep';

import { HelpMeChooseBanner } from './helpMeChooseBanner';
import { toSuggestionProduct } from './toSuggestionProduct';

import styles from './searchFullscreen.module.css';

export function SearchFullscreen() {
  const {
    hideSearchSuggestions,
    searchModalQuery,
    setSearchModalQuery,
    shouldOpenSearchModal,
    transition,
  } = useUserSearchQuery();
  const debouncedQuery = useDebounce(searchModalQuery, 300);

  const { featureFlags, goldConfig, pinnedStore, shoppingPreferences } =
    useRootLoaderData();
  const { fulfillmentMode } = useStoreSwitcher();
  const customLanguage = useCustomLanguage();
  const generatePath = useFullDestination();

  return (
    <div
      className={clsx(
        styles['container'],
        shouldOpenSearchModal === 'overlay' && transition
      )}
    >
      <Flex flexDirection="column" px={16}>
        <Flex
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
          width="100%"
          position="sticky"
        >
          <Box className={styles['dismissIcon']}>
            <Button.Icon
              ariaLabel="Go back"
              icon={<DismissIcon />}
              onClick={() => {
                setSearchModalQuery('');
                hideSearchSuggestions();
              }}
            />
          </Box>
          <div className={styles['searchText']}>
            <Typography variant="h2-semibold">Search</Typography>
          </div>
        </Flex>
        <Form
          className={styles['searchFieldBox']}
          action="/search"
          method="get"
          name="Search Form"
          onSubmit={async () => {
            await sleep(250);
            hideSearchSuggestions();
          }}
        >
          <SearchField
            borderRadius="lg"
            disableMobileInputStyling
            isDebounced={false}
            label="Search"
            name={SEARCH_TERM_QUERY_KEY}
            onChange={(val) => setSearchModalQuery(val)}
            placeholder={`Search ${pinnedStore.name}`}
            value={searchModalQuery}
            mb={16}
          />
        </Form>
        <HelpMeChooseBanner />
      </Flex>
      <SearchManager key={debouncedQuery} query={debouncedQuery}>
        {({ products, shouldFetchNextPage, totalProducts }) => (
          <SearchSuggestionsList
            isLoading={shouldFetchNextPage}
            onSearchLinkClick={hideSearchSuggestions}
            products={products.map((product) =>
              toSuggestionProduct({
                customLanguage,
                featureFlags,
                fulfillmentMode,
                generatePath,
                goldConfig,
                hideDropdown: hideSearchSuggestions,
                pinnedStore,
                product,
                shoppingPreferences,
              })
            )}
            query={searchModalQuery}
            searchUrl={generatePath(
              `/search?${new URLSearchParams({
                [SEARCH_TERM_QUERY_KEY]: searchModalQuery,
              })}`
            )}
            totalProducts={totalProducts}
          />
        )}
      </SearchManager>
    </div>
  );
}
